{
  "$schema": "https://cogo.xyz/ontology/v1/event-types",
  "version": "1.0.0",
  "description": "Canonical registry of event names and global event sources that blocks may bind to via events.json. Event types follow DOM standards; keyboard shortcuts are handled via flow conditionals on #{event.key}, NOT new event type names.",
  "events": {
    "tap": {
      "category": "pointer",
      "description": "Primary click / tap on an element (buttons, links, cards, etc.)",
      "payload": {},
      "appliesTo": ["interactive"]
    },
    "change": {
      "category": "form",
      "description": "Input, textarea, select value change",
      "payload": { "value": "any" },
      "appliesTo": ["input", "textarea", "select", "checkbox", "radio", "switch", "slider"]
    },
    "focus": {
      "category": "form",
      "description": "Input gained focus",
      "payload": {},
      "appliesTo": ["input", "textarea", "select"]
    },
    "blur": {
      "category": "form",
      "description": "Input lost focus",
      "payload": {},
      "appliesTo": ["input", "textarea", "select"]
    },
    "keydown": {
      "category": "keyboard",
      "description": "Key press event. Branch on #{event.key === 'Enter'} / 'Escape' / 'Tab' etc. in the flow — do NOT invent event type names like ':enter' or ':escape'.",
      "payload": { "key": "string", "metaKey": "boolean", "ctrlKey": "boolean", "shiftKey": "boolean", "altKey": "boolean", "value": "string" },
      "appliesTo": ["input", "textarea"]
    },
    "keyup": {
      "category": "keyboard",
      "description": "Key release event. Same payload as keydown.",
      "payload": { "key": "string", "metaKey": "boolean", "ctrlKey": "boolean", "shiftKey": "boolean", "altKey": "boolean", "value": "string" },
      "appliesTo": ["input", "textarea"]
    },
    "submit": {
      "category": "form",
      "description": "Form submit (explicit; inputs typically use keydown + Enter branch instead)",
      "payload": {},
      "appliesTo": ["form"]
    },
    "scroll": {
      "category": "pointer",
      "description": "Scrollable container scrolled",
      "payload": { "top": "number", "left": "number" },
      "appliesTo": ["container", "scroll-view"]
    },
    "rowClick": {
      "category": "table",
      "description": "Data table row clicked (anywhere outside a button/action column). Source of truth: cogo-react-renderer/src/renderers/data-table-renderer.tsx:126.",
      "payload": { "rowIndex": "number", "rowData": "object" },
      "appliesTo": ["data-table"]
    },
    "cellClick": {
      "category": "table",
      "description": "Data table cell-level button click (type:'button' column). Source of truth: cogo-react-renderer/src/renderers/data-table-renderer.tsx:309.",
      "payload": { "rowIndex": "number", "columnKey": "string", "value": "unknown", "row": "object" },
      "appliesTo": ["data-table"]
    },
    "actionClick": {
      "category": "table",
      "description": "Data table row-level action button click (edit, delete, view) inside an 'actions'-type column. Source of truth: cogo-react-renderer/src/renderers/data-table-renderer.tsx:370. Flow reads #{event.row.<field>} to access the full row object, #{event.actionId} to branch on which action button was pressed.",
      "payload": { "flowName": "string", "row": "object", "rowIndex": "number", "actionId": "string" },
      "appliesTo": ["data-table"]
    },
    "selectionChange": {
      "category": "table",
      "description": "Data table row selection change (multi-select checkbox). RESERVED — not yet dispatched by cogo-react-renderer (selection state is currently local to the renderer). Do NOT bind to this event until the renderer emits it; use an explicit per-row checkbox binding instead.",
      "payload": null,
      "appliesTo": ["data-table"]
    },
    "cellChange": {
      "category": "table",
      "description": "Data table editable cell value committed (edit-mode inputs/selects/checkboxes). Source of truth: cogo-react-renderer/src/renderers/data-table-renderer.tsx:134.",
      "payload": { "rowIndex": "number", "columnKey": "string", "value": "unknown", "previousValue": "unknown", "rowData": "object" },
      "appliesTo": ["data-table"]
    },
    "close": {
      "category": "overlay",
      "description": "Dismiss event from dialog/sheet/drawer/popover when user closes via X button, backdrop click, or Escape. Source of truth: cogo-react-renderer/src/renderers/{dialog,sheet,drawer,popover}-renderer.tsx — empty payload object dispatched on close.",
      "payload": {},
      "appliesTo": ["dialog", "sheet", "drawer", "popover", "alert-dialog"]
    },
    "openChange": {
      "category": "overlay",
      "description": "Controlled open-state transition dispatched by overlay and select renderers.",
      "payload": { "open": "boolean" },
      "appliesTo": ["dialog", "alert-dialog", "sheet", "drawer", "popover", "select"]
    },
    "toggle": {
      "category": "form",
      "description": "Accordion open-item collection changed.",
      "payload": { "value": "array", "openItems": "array" },
      "appliesTo": ["accordion"]
    }
  },
  "globalEventSources": {
    "document": {
      "description": "Document-level listener. Use for global keyboard shortcuts (⌘K, ⌘/, Escape overlays).",
      "validEvents": ["keydown", "keyup", "visibilitychange"],
      "dispatchedBy": "AppV2 runtime (addEventListener on document)",
      "example": { "binding": "document:keydown", "flow": "my-block.onGlobalKeydown", "handlerBranch": "#{(event.metaKey || event.ctrlKey) && event.key === 'k'}" }
    },
    "window": {
      "description": "Window-level listener. Use for resize, popstate, focus/blur at the window level.",
      "validEvents": ["resize", "popstate", "focus", "blur", "online", "offline"],
      "dispatchedBy": "AppV2 runtime (addEventListener on window)"
    }
  },
  "conventions": {
    "noInventedEventNames": "NEVER dispatch or bind to event names that are not in this ontology. For keyboard shortcuts, bind :keydown and branch on #{event.key} inside the flow.",
    "specialEvents": "Only registered events, including openChange for controlled overlays/selects and toggle for accordions, plus explicit global sources, are supported. New event types require ontology registration.",
    "rowEvents": "Data-table row-level events (e.g., 'table:editRow', 'table:deleteRow') are exempt from element-exists validation but follow the same naming rule."
  }
}
